import { useMemo, useState } from 'react';
import { Modal, Button } from '@/components';
import type { EmailRecipient, PickedContact } from '@/lib/apiClient';
import RecipientsField, { mailtoFor, reusableRecipients, startingRecipients } from './contacts/RecipientsField';
import { useJobContacts } from './contacts/jobContacts';
import { fmtDate } from '@/lib/dates';
import { reviewDueStatus } from './submittalStatus';
import { revisionLabel } from './submittalRounds';

/**
 * Client-side Outlook draft composer for submittals (PM-controlled).
 *
 * Mirrors the PPR EmailGeneratorModal pattern: build an editable Subject + Body,
 * open a `mailto:` href ("✉ Open in Outlook"), offer "Copy subject"/"Copy body"
 * via the clipboard, and warn when the encoded mailto runs long (clients
 * truncate the body past ~2000 chars). The app never sends mail and makes no
 * API call here — the PM attaches files and sends from Outlook.
 *
 * ⚠️ Opening this does NOT record a send (#626). It used to follow a call that moved
 * the submittal into review before the draft existed, so the record said "sent" while
 * the email sat in Drafts. The parent now asks afterwards, with the time, which is why
 * onClose reports whether the draft was used.
 *
 * Three modes:
 *   • send     — cover note for sending the submittal to the reviewer.
 *   • reminder — polite nudge keyed to the review-due date (overdue / due soon).
 *   • revision — cover note for re-sending a corrected package for re-review.
 */

interface SubmittalEmailSub {
  id: number;
  title: string;
  specSection: string | null;
  submittalType: string | null;
  reviewer: string | null;
  reviewerContact?: PickedContact | null;
  reviewDue: string | null;
  /** The review round a revision draft is for (Rev 1, Rev 2…), when known. */
  revision?: number;
}

interface SubmittalEmailProject {
  jobNumber: string;
  title: string;
}

export interface SubmittalEmailModalProps {
  sub: SubmittalEmailSub;
  project: SubmittalEmailProject;
  /** The job, for its contacts (#598 step 2). */
  projectId: number;
  /**
   * Who the last round's send went to, for a reminder or a revision: it starts
   * addressed to them rather than to the reviewer alone, so whoever was copied
   * isn't dropped (review).
   */
  previousRecipients?: EmailRecipient[];
  mode: 'send' | 'reminder' | 'revision';
  /**
   * `used` is whether the draft went anywhere: "Open in Outlook" clicked, or the
   * subject or body copied. For a send or a revision the parent asks "did you send it?"
   * only then (#626); a composer closed untouched cannot have sent anything.
   * `recipients` is who the draft was addressed to when it was closed (#598 step 2).
   */
  onClose: (used: boolean, recipients: EmailRecipient[]) => void;
}

const css = `
  .sub-em-field { display: flex; flex-direction: column; gap: 4px; }
  .sub-em-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase; letter-spacing: var(--ew-ls-caption); color: var(--ew-n-600);
  }
  .sub-em-input, .sub-em-textarea {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    padding: 8px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    background: #fff; color: var(--ew-dark); width: 100%; box-sizing: border-box;
  }
  .sub-em-textarea { resize: vertical; min-height: 220px; font-family: var(--ew-font); }
  .sub-em-label-row { display: flex; justify-content: space-between; align-items: center; }
  .sub-em-copied { color: #027835; font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font); }
  .sub-em-linkbtn {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-600); font: var(--ew-fw-medium) var(--ew-fs-caption) var(--ew-font);
    text-decoration: underline; text-underline-offset: 2px;
  }
  .sub-em-linkbtn:hover { color: var(--ew-dark); }
  .sub-em-hint { color: var(--ew-n-500); font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .sub-em-warn { color: #8a5a00; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .sub-em-error { color: #8F2414; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .sub-em-footer-note { margin-right: auto; max-width: 320px; }
`;

function buildSendSubject(sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  const spec = sub.specSection ? ` (${sub.specSection})` : '';
  return `Submittal for Review — ${sub.title}${spec} — ${project.jobNumber} ${project.title}`;
}

function buildSendBody(sub: SubmittalEmailSub): string {
  const lines: string[] = [];
  lines.push('Hello,');
  lines.push('');
  lines.push('Please find the following submittal for your review:');
  lines.push('');
  lines.push(`  • Submittal: ${sub.title}`);
  if (sub.specSection) lines.push(`  • Spec section: ${sub.specSection}`);
  if (sub.submittalType) lines.push(`  • Type: ${sub.submittalType}`);
  lines.push('');
  if (sub.reviewDue) {
    lines.push(`We would appreciate your review by ${fmtDate(sub.reviewDue)}.`);
    lines.push('');
  }
  lines.push('The submittal documents are attached.');
  lines.push('');
  lines.push('Please let me know if you have any questions or need anything further.');
  lines.push('');
  lines.push('Thank you,');
  return lines.join('\n');
}

function buildReminderSubject(sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  const { daysUntil } = reviewDueStatus(sub.reviewDue);
  const overdue = daysUntil != null && daysUntil < 0;
  const tail = overdue ? 'Overdue' : `Due ${fmtDate(sub.reviewDue)}`;
  return `Reminder — Submittal ${sub.title} — ${tail} — ${project.jobNumber}`;
}

function buildReminderBody(sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  const { daysUntil } = reviewDueStatus(sub.reviewDue);
  const lines: string[] = [];
  lines.push('Hello,');
  lines.push('');

  let statusSentence: string;
  if (daysUntil == null) {
    statusSentence = `I wanted to follow up on the submittal "${sub.title}" for ${project.jobNumber} ${project.title}.`;
  } else if (daysUntil < 0) {
    const n = Math.abs(daysUntil);
    statusSentence = `The submittal "${sub.title}" for ${project.jobNumber} ${project.title} was due on ${fmtDate(sub.reviewDue)} and is now overdue by ${n} day${n === 1 ? '' : 's'}.`;
  } else if (daysUntil === 0) {
    statusSentence = `The submittal "${sub.title}" for ${project.jobNumber} ${project.title} is due today (${fmtDate(sub.reviewDue)}).`;
  } else {
    statusSentence = `The submittal "${sub.title}" for ${project.jobNumber} ${project.title} is due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}, on ${fmtDate(sub.reviewDue)}.`;
  }
  lines.push(statusSentence);
  lines.push('');
  lines.push('Could you please let me know the status of your review, or send the reviewed submittal back when you have a chance?');
  lines.push('');
  lines.push('Thank you for your help,');
  return lines.join('\n');
}

function buildRevisionSubject(sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  const spec = sub.specSection ? ` (${sub.specSection})` : '';
  const rev = sub.revision != null ? ` — ${revisionLabel(sub.revision)}` : '';
  return `Revised Submittal for Review${rev} — ${sub.title}${spec} — ${project.jobNumber} ${project.title}`;
}

function buildRevisionBody(sub: SubmittalEmailSub): string {
  const lines: string[] = [];
  lines.push('Hello,');
  lines.push('');
  const rev = sub.revision != null ? ` (${revisionLabel(sub.revision)})` : '';
  lines.push(`Please find the revised submittal${rev}, updated per your review comments:`);
  lines.push('');
  lines.push(`  • Submittal: ${sub.title}`);
  if (sub.specSection) lines.push(`  • Spec section: ${sub.specSection}`);
  if (sub.submittalType) lines.push(`  • Type: ${sub.submittalType}`);
  lines.push('');
  if (sub.reviewDue) {
    lines.push(`We would appreciate your re-review by ${fmtDate(sub.reviewDue)}.`);
    lines.push('');
  }
  lines.push('The revised documents are attached.');
  lines.push('');
  lines.push('Please let me know if you have any questions or need anything further.');
  lines.push('');
  lines.push('Thank you,');
  return lines.join('\n');
}

const MODE_TITLE: Record<SubmittalEmailModalProps['mode'], string> = {
  send: 'Send submittal with Outlook',
  reminder: 'Send a reminder',
  revision: 'Send revision with Outlook',
};

function buildSubjectFor(mode: SubmittalEmailModalProps['mode'], sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  if (mode === 'revision') return buildRevisionSubject(sub, project);
  if (mode === 'reminder') return buildReminderSubject(sub, project);
  return buildSendSubject(sub, project);
}

function buildBodyFor(mode: SubmittalEmailModalProps['mode'], sub: SubmittalEmailSub, project: SubmittalEmailProject): string {
  if (mode === 'revision') return buildRevisionBody(sub);
  if (mode === 'reminder') return buildReminderBody(sub, project);
  return buildSendBody(sub);
}

export default function SubmittalEmailModal({ sub, project, projectId, mode, onClose, previousRecipients }: SubmittalEmailModalProps) {
  // The opener waits for these before offering a reminder or a revision, so they are
  // here (from the shared query) when the recipients below are first worked out.
  const { options: contacts, isSuccess: contactsKnown } = useJobContacts(projectId);
  // Whoever the last round went to, else a reviewer who is a job contact on the To line (#598 step 2).
  const [recipients, setRecipients] = useState<EmailRecipient[]>(() =>
    {
    const reused = previousRecipients?.length ? reusableRecipients(previousRecipients, contactsKnown ? contacts : null) : [];
    return reused.length ? reused : startingRecipients(sub.reviewerContact);
  });
  const [subject, setSubject] = useState(() => buildSubjectFor(mode, sub, project));
  const [body, setBody] = useState(() => buildBodyFor(mode, sub, project));
  const [copied, setCopied] = useState<'subject' | 'body' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [used, setUsed] = useState(false);

  // mailto: draft link (opens the OS default mail client, e.g. Outlook desktop).
  // To and CC as picked, encoded subject + body — same mechanics as RfiEmailModal.
  const mailtoHref = useMemo(() => mailtoFor(recipients, subject, body), [recipients, subject, body]);
  // mailto URLs get truncated by many clients past ~2000 chars (the encoded body
  // inflates length); warn so the PM uses Copy for long emails.
  const mailtoTooLong = mailtoHref.length > 1900;

  async function copy(which: 'subject' | 'body') {
    try {
      await navigator.clipboard.writeText(which === 'subject' ? subject : body);
      setCopied(which);
      setUsed(true);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('Could not access the clipboard — select the text and copy manually.');
    }
  }

  return (
    <>
      <style>{css}</style>
      <Modal
        isOpen
        onClose={() => onClose(used, recipients)}
        maxWidth={720}
        title={MODE_TITLE[mode]}
        footer={
          <>
            {/* The send/revision bodies both assert the documents are attached,
                so the reminder has to be here. A reminder email carries nothing,
                hence the mode check. */}
            {mode === 'reminder' ? null : (
              <span className="sub-em-hint sub-em-footer-note">
                📎 Attach the submittal documents in Outlook — the email says they're attached.
              </span>
            )}
            <a className="btn btn-primary ew-btn-sm" href={mailtoHref} target="_blank" rel="noopener noreferrer" onClick={() => setUsed(true)} onAuxClick={(e) => { if (e.button === 1) setUsed(true); }}>
              ✉ Open in Outlook
            </a>
            <Button variant="secondary" size="sm" onClick={() => onClose(used, recipients)}>Done</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
          <RecipientsField contacts={contacts} value={recipients} onChange={setRecipients} />
          {sub.reviewerContact && !sub.reviewerContact.email ? (
            <span className="sub-em-hint">
              Reviewer: {sub.reviewerContact.name ?? sub.reviewer}, who has no email in the address book. Add it on the job’s Contacts tab, or type the address.
            </span>
          ) : !sub.reviewerContact && sub.reviewer ? (
            <span className="sub-em-hint">Reviewer on file: {sub.reviewer}. Pick them from the job’s contacts to address it for you.</span>
          ) : null}

          <div className="sub-em-field">
            <div className="sub-em-label-row">
              <label>Subject</label>
              <button type="button" className="sub-em-linkbtn" onClick={() => copy('subject')}>
                {copied === 'subject' ? <span className="sub-em-copied">Copied!</span> : 'Copy subject'}
              </button>
            </div>
            <input className="sub-em-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div className="sub-em-field">
            <div className="sub-em-label-row">
              <label>Body</label>
              <button type="button" className="sub-em-linkbtn" onClick={() => copy('body')}>
                {copied === 'body' ? <span className="sub-em-copied">Copied!</span> : 'Copy body'}
              </button>
            </div>
            <textarea className="sub-em-textarea" value={body} onChange={(e) => setBody(e.target.value)} />
            {mailtoTooLong && (
              <span className="sub-em-warn">
                This email is long — "Open in Outlook" may truncate it. Use "Copy body" and paste it in instead.
              </span>
            )}
          </div>

          {error && <div className="sub-em-error">{error}</div>}
        </div>
      </Modal>
    </>
  );
}
